import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Lenis from "lenis";
import { Menu, X } from "lucide-react";
import { LANGS, T } from "../i18n";

export const useLang = () => {
  const { lang } = useParams();
  const l = LANGS.includes(lang) ? lang : "fr";
  return { lang: l, t: T[l] };
};

const LangSwitcher = ({ lang, className = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const switchTo = (l) => {
    const rest = location.pathname.replace(/^\/(fr|en|es|pt)/, "");
    navigate(`/${l}${rest || "/"}`);
  };
  return (
    <div data-testid="language-selector" className={`flex items-center gap-1 ${className}`}>
      {LANGS.map((l, i) => (
        <span key={l} className="flex items-center gap-1">
          <button
            data-testid={`language-selector-${l}`}
            onClick={() => switchTo(l)}
            className={`px-1.5 py-1 text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
              l === lang ? "text-brand-gold" : "text-white/40 hover:text-white"
            }`}
          >
            {l}
          </button>
          {i < LANGS.length - 1 && <span className="text-white/15 text-[9px]">|</span>}
        </span>
      ))}
    </div>
  );
};

const Header = ({ lang, t }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: `/${lang}/`, label: t.nav.home, end: true },
    { to: `/${lang}/services`, label: t.nav.method },
    { to: `/${lang}/kit-digital-premium`, label: t.nav.kit },
    { to: `/${lang}/a-propos`, label: t.nav.about },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-brand-ink/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-10 py-4">
        <Link data-testid="logo-link" to={`/${lang}/`} className="flex items-center gap-2">
          <span className="text-brand-gold text-xs">◆</span>
          <span className="font-serif text-xl tracking-[0.25em] text-white">AETHERIS</span>
        </Link>

        <nav data-testid="main-nav" className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors duration-200 ${
                  isActive ? "text-brand-gold" : "text-white/60 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <LangSwitcher lang={lang} />
          <Link
            data-testid="header-cta-bilan"
            to={`/${lang}/bilan`}
            className="bg-brand-gold text-brand-ink px-5 py-2.5 text-xs font-semibold uppercase tracking-wide transition-all duration-300 hover:bg-brand-gold-hover"
          >
            {t.nav.bilan}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <Menu className="hidden" /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-brand-ink/95 backdrop-blur-xl">
          <div className="flex flex-col gap-1 px-6 py-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className="py-3 text-sm text-white/70 hover:text-white border-b border-white/5"
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink to={`/${lang}/bilan`} className="py-3 text-sm text-brand-gold border-b border-white/5">
              {t.nav.bilan}
            </NavLink>
            <div className="pt-4">
              <LangSwitcher lang={lang} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = ({ lang, t }) => (
  <footer data-testid="site-footer" className="border-t border-white/10 bg-brand-ink">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16">
      <div className="grid gap-12 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-brand-gold text-xs">◆</span>
            <span className="font-serif text-lg tracking-[0.25em] text-white">AETHERIS</span>
          </div>
          <p className="text-sm font-light text-white/50 leading-relaxed">{t.footer.tagline}</p>
        </div>
        <div>
          <p className="uppercase tracking-luxe text-[11px] text-brand-gold mb-4">{t.footer.navTitle}</p>
          <div className="flex flex-col gap-2">
            <Link to={`/${lang}/`} className="text-sm text-white/50 hover:text-white transition-colors">{t.nav.home}</Link>
            <Link to={`/${lang}/services`} className="text-sm text-white/50 hover:text-white transition-colors">{t.nav.method}</Link>
            <Link to={`/${lang}/kit-digital-premium`} className="text-sm text-white/50 hover:text-white transition-colors">{t.nav.kit}</Link>
            <Link to={`/${lang}/a-propos`} className="text-sm text-white/50 hover:text-white transition-colors">{t.nav.about}</Link>
            <Link to={`/${lang}/bilan`} className="text-sm text-white/50 hover:text-white transition-colors">{t.nav.bilan}</Link>
          </div>
        </div>
        <div>
          <p className="uppercase tracking-luxe text-[11px] text-brand-gold mb-4">{t.footer.langTitle}</p>
          <LangSwitcher lang={lang} />
        </div>
      </div>
      <div className="mt-14 border-t border-white/10 pt-8">
        <p data-testid="footer-legal" className="text-xs font-light text-white/40 leading-relaxed">{t.footer.svh}</p>
        <p className="mt-2 text-xs font-light text-white/30">{t.footer.rights}</p>
      </div>
    </div>
  </footer>
);

const Layout = () => {
  const { lang, t } = useLang();
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 0.9 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-brand-ink text-white">
      <Header lang={lang} t={t} />
      <main>
        <Outlet />
      </main>
      <Footer lang={lang} t={t} />
    </div>
  );
};

export default Layout;
